import React, { useState, useEffect, useRef } from 'react';
import Textarea from '../../../components/ui/Textarea';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import notificationService from '../../../services/notificationService';
import { EMAIL_EVENTS, getEmailEvent, renderEmailEvent } from '../../../services/emailTemplates';

/**
 * Conference Management → Emails tab.
 * Lets organizers/chairs customize the automated emails the platform sends
 * (submission received, accepted, rejected, reviewer assignment, etc.).
 * Each event falls back to a built-in default when not customized.
 */
const EmailTemplatesTab = ({ conferenceId }) => {
  const { t } = useTranslation('notifications');
  const k = (key, opts) => t(`conferenceEmails.${key}`, opts);

  const [customByKey, setCustomByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState({ subject: '', message: '' });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const messageRef = useRef(null);
  const subjectRef = useRef(null);
  const lastFocused = useRef('message');

  useEffect(() => {
    loadTemplates();
  }, [conferenceId]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const byKey = await notificationService?.getEventTemplates(conferenceId);
      setCustomByKey(byKey || {});
    } finally {
      setLoading(false);
    }
  };

  const eventLabel = (eventKey) => k(`events.${eventKey}.label`);
  const eventDescription = (eventKey) => k(`events.${eventKey}.description`);
  const recipientLabel = (recipient) => k(`recipients.${recipient?.toLowerCase()}`);

  const startEditing = (eventKey) => {
    const event = getEmailEvent(eventKey);
    const custom = customByKey?.[eventKey];
    setForm({
      subject: custom?.subject || event?.defaultSubject || '',
      message: custom?.message || event?.defaultMessage || ''
    });
    setFeedback(null);
    setEditingKey(eventKey);
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setForm({ subject: '', message: '' });
    setFeedback(null);
  };

  const insertVariable = (variableName) => {
    const token = `{{${variableName}}}`;
    const field = lastFocused?.current === 'subject' ? 'subject' : 'message';
    const ref = field === 'subject' ? subjectRef : messageRef;
    const el = ref?.current;
    if (!el) {
      setForm(prev => ({ ...prev, [field]: `${prev?.[field] || ''}${token}` }));
      return;
    }
    const start = el?.selectionStart ?? (form?.[field]?.length || 0);
    const end = el?.selectionEnd ?? start;
    const text = form?.[field] || '';
    const newText = text.substring(0, start) + token + text.substring(end);
    setForm(prev => ({ ...prev, [field]: newText }));
    setTimeout(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  };

  const handleSave = async () => {
    if (!form?.subject?.trim() || !form?.message?.trim()) {
      setFeedback({ type: 'error', text: k('requiredFields') });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      await notificationService?.upsertEventTemplate(conferenceId, editingKey, {
        name: eventLabel(editingKey),
        subject: form?.subject,
        message: form?.message
      });
      await loadTemplates();
      setFeedback({ type: 'success', text: k('saved') });
    } catch (err) {
      setFeedback({ type: 'error', text: err?.message || k('saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(k('confirmReset'))) return;
    setResetting(true);
    setFeedback(null);
    try {
      await notificationService?.resetEventTemplate(conferenceId, editingKey);
      await loadTemplates();
      const event = getEmailEvent(editingKey);
      setForm({ subject: event?.defaultSubject || '', message: event?.defaultMessage || '' });
      setFeedback({ type: 'success', text: k('reverted') });
    } catch (err) {
      setFeedback({ type: 'error', text: err?.message || k('resetFailed') });
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={36} className="text-primary animate-spin" />
      </div>
    );
  }

  // ── Editor view ──
  if (editingKey) {
    const event = getEmailEvent(editingKey);
    const preview = renderEmailEvent(editingKey, form, sampleVars(event));

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={cancelEditing}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1"
            >
              <Icon name="ChevronLeft" size={14} /> {k('back')}
            </button>
            <h2 className="text-lg font-semibold text-foreground">{eventLabel(editingKey)}</h2>
            <p className="text-xs text-muted-foreground">{eventDescription(editingKey)}</p>
          </div>
          <span className="px-2.5 py-1 text-xs font-medium bg-muted text-muted-foreground">
            {k('to', { recipient: recipientLabel(event?.recipient) })}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Editor */}
          <div className="lg:col-span-2 space-y-4">
            <Input
              label={k('subject')}
              ref={subjectRef}
              value={form?.subject}
              onFocus={() => { lastFocused.current = 'subject'; }}
              onChange={(e) => setForm(prev => ({ ...prev, subject: e?.target?.value }))}
              placeholder={event?.defaultSubject}
            />
            <div>
              <Textarea
                ref={messageRef}
                label={k('message')}
                value={form?.message || ''}
                onFocus={() => { lastFocused.current = 'message'; }}
                onChange={(e) => setForm(prev => ({ ...prev, message: e?.target?.value }))}
                rows={14}
                placeholder={event?.defaultMessage}
                className="font-mono"
              />
            </div>

            {feedback && (
              <p className={`text-sm ${feedback?.type === 'error' ? 'text-error' : 'text-success'}`}>
                {feedback?.text}
              </p>
            )}

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <Button
                variant="ghost"
                onClick={handleReset}
                disabled={resetting || saving || !customByKey?.[editingKey]}
                iconName="RotateCcw"
                iconPosition="left"
              >
                {resetting ? k('resetting') : k('reset')}
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={cancelEditing} disabled={saving}>{k('cancel')}</Button>
                <Button onClick={handleSave} disabled={saving} iconName="Save" iconPosition="left">
                  {saving ? k('saving') : k('save')}
                </Button>
              </div>
            </div>
          </div>

          {/* Variables key table + preview */}
          <div className="space-y-4">
            <div className="bg-muted/40 border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Code" size={16} className="text-primary" />
                <h3 className="text-sm font-semibold text-foreground">{k('variablesTitle')}</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {k('variablesHint')}
              </p>
              <div className="space-y-1">
                {event?.variables?.map((v) => (
                  <button
                    key={v?.name}
                    onClick={() => insertVariable(v?.name)}
                    className="w-full text-start px-3 py-2 bg-background hover:bg-muted border border-border transition-colors"
                  >
                    <code className="text-sm font-mono text-primary">{`{{${v?.name}}}`}</code>
                    <p className="text-xs text-muted-foreground mt-0.5">{k(`variables.${v?.name}`)}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-2">{k('previewTitle')}</h3>
              <p className="text-xs font-medium text-foreground">{preview?.subject}</p>
              <div className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap border-t border-border pt-2">
                {preview?.message}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── List view ──
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">{k('tabTitle')}</h2>
        <p className="text-xs text-muted-foreground mt-1">
          {k('tabSubtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-2">
        {EMAIL_EVENTS?.map((event) => {
          const isCustomized = !!customByKey?.[event?.key];
          return (
            <div
              key={event?.key}
              className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-card hover:shadow-sm transition-250"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground">{eventLabel(event?.key)}</span>
                  <span className="px-2 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
                    {k('to', { recipient: recipientLabel(event?.recipient) })}
                  </span>
                  {isCustomized ? (
                    <span className="px-2 py-0.5 text-[10px] font-medium bg-success/10 text-success">
                      {k('customized')}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
                      {k('default')}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{eventDescription(event?.key)}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => startEditing(event?.key)}
                iconName="Pencil"
                iconPosition="left"
              >
                {k('edit')}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Sample values so the editor preview shows realistic output. */
const sampleVars = (event) => {
  const samples = {
    authorName: 'Jane Doe',
    reviewerName: 'Dr. Alan Smith',
    chairName: 'Prof. Maria Lopez',
    paperTitle: 'A Study on Adaptive Reuse in Heritage Architecture',
    conferenceName: 'Your Conference',
    participantId: 'CONF-0042',
    decision: 'Minor Revisions',
    reviewLink: `${window.location.origin}/reviewer-dashboard`
  };
  const vars = {};
  event?.variables?.forEach(v => { vars[v?.name] = samples?.[v?.name] ?? `{{${v?.name}}}`; });
  return vars;
};

export default EmailTemplatesTab;
